import type { QueryKey } from '@tanstack/react-query';

import { queryClient } from '@/query/queryClient';

/**
 * The one place for query keys (UI state plan, phase 0).
 *
 * Every key of role-dependent data starts with `['a', <active assignment id>]`,
 * so data loaded under one role can never be shown under another: after a role
 * switch the keys differ (and `clearSession()` drops the cache as well).
 *
 * The session's own roots are the two things known BEFORE a role is active
 * (who is signed in, and which role is active); they start with `['session']`.
 * Everything the active role decides (permissions map, landing modules, master
 * data, every page's data) is under the assignment id.
 *
 * No React and no ApiProvider import here (ApiProvider imports this module).
 */
export const ASSIGNMENT_ROOT = 'a';
export const SESSION_ROOT = 'session';

export const sessionKeys = {
  all: [SESSION_ROOT] as const,
  /** the signed-in user (LoginData `user`) */
  user: [SESSION_ROOT, 'user'] as const,
  /** GET me/assignments: this user's assignments + `active_assignment_id` */
  assignments: [SESSION_ROOT, 'assignments'] as const,
  /** the ACTIVE assignment with its context, confirmed by the server */
  activeAssignment: [SESSION_ROOT, 'active-assignment'] as const,
  /** LoginData `active_context` (the fallback when me/assignments is unavailable) */
  loginContext: [SESSION_ROOT, 'login-context'] as const,
  /** LoginData `default_filter`: the user's default saved filter (JSON text), or null */
  defaultFilter: [SESSION_ROOT, 'default-filter'] as const,
};

/** The active assignment's id, or null (signed out, or no role chosen yet). */
export const activeAssignmentId = (): number | null => {
  const active = queryClient.getQueryData<{ id?: unknown } | null>(sessionKeys.activeAssignment);
  const id = Number(active?.id);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/** `['a', assignmentId, ...parts]`: a key of data that belongs to one role assignment. */
export const assignmentKey = (assignmentId: number | null, ...parts: unknown[]): QueryKey => [
  ASSIGNMENT_ROOT,
  assignmentId,
  ...parts,
];

/** A key under the ACTIVE assignment: `['a', <active id>, ...parts]`. */
export const activeKey = (...parts: unknown[]): QueryKey =>
  assignmentKey(activeAssignmentId(), ...parts);

/** Everything loaded for one assignment (prefix for remove / invalidate). */
export const assignmentScope = (assignmentId: number | null): QueryKey => [
  ASSIGNMENT_ROOT,
  assignmentId,
];

/** The active role's session data (loaded at sign-in, on a role switch and on a hard refresh). */
export const roleSessionKeys = {
  /** GET User/PermissionsMap: the permissions the role holds (`name: true`) */
  permissions: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, SESSION_ROOT, 'permissions'),
  /** identity me/details: the landing modules (home / brand / partner) */
  modules: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, SESSION_ROOT, 'modules'),
  /** GET GetMasterCommanData: master lookups of the forms */
  commonData: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, SESSION_ROOT, 'common-data'),
  /** a RIGHT_HOLDER role's start partner (auth/landing.ts) */
  homePartner: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, SESSION_ROOT, 'home-partner'),
};

/**
 * The header selection of one role assignment (header brand, header partner,
 * the applied global filter): selection/headerSelection.ts. Not a session
 * key: a role switch drops it with the rest of the role's data.
 */
export const HEADER_SELECTION = 'header-selection';

export const headerSelectionKey = (assignmentId: number | null = activeAssignmentId()): QueryKey =>
  assignmentKey(assignmentId, HEADER_SELECTION);

export const isHeaderSelectionKey = (key: QueryKey): boolean =>
  Array.isArray(key) && key[0] === ASSIGNMENT_ROOT && key[2] === HEADER_SELECTION;

/** True when `key` belongs to role assignment `assignmentId`. */
export const isAssignmentKey = (key: QueryKey, assignmentId: number | null): boolean =>
  Array.isArray(key) && key[0] === ASSIGNMENT_ROOT && key[1] === assignmentId;

/**
 * The notification centre (pnet-docs notifications/Notification-Centre-Design.md): the bell's
 * summary, the lists (panel and page, one key per view / area) and the person's subscriptions. Under
 * the active assignment like every role-dependent answer; `all` is the prefix of every one of them.
 */
export const NOTIFICATIONS = 'notifications';

export const notificationKeys = {
  all: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, NOTIFICATIONS),
  /** GET me/notifications/summary: `{ unread, needs_action }` */
  summary: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, NOTIFICATIONS, 'summary'),
  /** every list (prefix): the panel's and the page's */
  lists: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, NOTIFICATIONS, 'list'),
  /** GET me/notifications?view=&area= (paged by cursor) */
  list: (
    params: { view: string; area: string | null; limit: number },
    assignmentId: number | null = activeAssignmentId(),
  ): QueryKey => assignmentKey(assignmentId, NOTIFICATIONS, 'list', params),
  /** GET me/subscriptions (useAccessQuery endpoint NOTIFICATIONS, params 'subscriptions') */
  subscriptions: (assignmentId: number | null = activeAssignmentId()): QueryKey =>
    assignmentKey(assignmentId, NOTIFICATIONS, 'subscriptions'),
};
